import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';

import { Avatar, Button, HStack, Text, VStack, theme } from '@squire/ui';

import { Card } from '@/components/_shared/Card';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { formatAddressToString } from '@/utils/addressUtils';
import { formatDistance } from '@/utils/distanceUtils';

import { styles } from './styles';
import type { SearchResultCardProps } from './types';

const SearchResultCard = ({
  shop,
  onPress,
  onDirectionsPress,
  testID,
  prioritizeAlias = false,
}: SearchResultCardProps) => {
  const { t, i18n } = useScreenTranslations('HomeScreen');

  const distance = useMemo(() => {
    if (shop.distance_miles) {
      return formatDistance(shop.distance_miles, i18n.language);
    }
    return null;
  }, [shop.distance_miles, i18n.language]);

  // `prioritizeAlias` leads with the alias, which distinguishes branches of a
  // brand; the name still follows, because an alias carries no location and is
  // often null, and the name is the label every other screen shows.
  const primaryLabel = prioritizeAlias ? shop.alias || shop.name : shop.name;
  const secondaryLabel = prioritizeAlias ? shop.name : shop.alias;
  // With no alias the primary label falls back to the name — don't repeat it.
  const showSecondaryLabel =
    !!secondaryLabel && secondaryLabel !== primaryLabel;

  return (
    // No button role on the card: it wraps the Get directions button, and a
    // button inside a button is invalid HTML. The name still reaches the
    // focusable card.
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityLabel={t('accessibility.barbershopCard', {
        name: primaryLabel,
        address: formatAddressToString(shop.address),
        distance: distance ?? '',
      })}
    >
      {({ pressed }) => (
        <Card
          style={[styles.container, pressed && styles.containerPressed]}
          paddingVertical={theme.spacing[5]}
          paddingHorizontal={theme.spacing[5]}
        >
          <HStack space={theme.spacing[6]} style={styles.contentWrapper}>
            <View style={styles.avatar}>
              <Avatar
                size="2xl"
                shape="square"
                fallbackText={primaryLabel}
                uri={shop.photos?.at(0)?.url}
              />
            </View>

            <VStack space={theme.spacing[3]} style={styles.content}>
              <VStack>
                <Text
                  numberOfLines={1}
                  variant="lStrong"
                  color={pressed ? 'textOnBrand' : 'textPrimary'}
                >
                  {primaryLabel}
                </Text>
                {showSecondaryLabel && (
                  <Text
                    numberOfLines={1}
                    variant="m"
                    color={pressed ? 'textOnBrand' : 'textPrimary'}
                  >
                    {secondaryLabel}
                  </Text>
                )}
              </VStack>
              <Text
                numberOfLines={2}
                variant="s"
                ellipsizeMode="tail"
                color={pressed ? 'textOnBrand' : 'textSecondary'}
              >
                {formatAddressToString(shop.address)}
              </Text>

              <View style={styles.divider} />

              <HStack centerVertical style={styles.footerContainer}>
                <Button
                  variant="textLink"
                  size="small"
                  style={styles.directionsButton}
                  onPress={onDirectionsPress}
                >
                  {t('getDirections') as string}
                </Button>
                {distance && (
                  <Text
                    variant="s"
                    color={pressed ? 'textOnBrand' : 'textSecondary'}
                    numberOfLines={1}
                  >
                    ~{distance}
                  </Text>
                )}
              </HStack>
            </VStack>
          </HStack>
        </Card>
      )}
    </Pressable>
  );
};

SearchResultCard.displayName = 'SearchResultCard';
export { SearchResultCard };
